import { Observable } from 'rxjs';
import { concatMap, finalize } from 'rxjs/operators';
import { logQuiz } from 'logger';
import timesync from 'timesync';
import { Queue, LinkedList } from 'collections';
import { setCursor, setQuestion, setStopLoading, setLoaded } from './state';
import { delay as waitFor } from './delay';
const ISCOMPLETE = 1;
const ISCORRECT = 2;
const NE_ANSWER = 3;
const STEP = 17;
const fixup = (question, foraudit = false) => {
    const chosen = question.distractororder.filter((key) => {
        return question.distractordata[key].chosen;
    });
    if (chosen.length > 0) {
        const value = chosen[0];
        const idx = question.distractororder.indexOf(+value);
        question.answer = `${value}`;
        question.flags[ISCOMPLETE] = true;
        if (foraudit) {
            const iscorrect = question.flags[idx + 4];
            question.marked = !iscorrect;
        }
    }
    else if (question.questiontype === 'NumericEntry') {
        const value = question.answer === undefined || question.answer === null ? question.flags[NE_ANSWER] : question.answer;
        if (value !== undefined && value !== null) {
            question.answer = `${value}`;
            question.flags[ISCOMPLETE] = true;
        }
        else {
            question.flags[ISCOMPLETE] = false;
        }
        if (foraudit) {
            question.marked = !question.flags[ISCORRECT];
        }
    }
    else {
        question.flags[ISCOMPLETE] = false;
        if (foraudit) {
            question.marked = true;
        }
    }
    return question;
};
export class QueueLoader {
    _queue;
    _list;
    svc;
    numberofquestions;
    firstquestion;
    instanceId;
    loader;
    stoppedloading;
    isrunning;
    useQueue;
    resultid;
    quizstore;
    constructor({ svc, numberofquestions, instanceId, useQueue, quizstore, resultid, firstquestion = 0 }) {
        if (useQueue) {
            this._queue = new Queue();
        }
        else {
            this._list = new LinkedList();
        }
        this.svc = svc;
        this.numberofquestions = numberofquestions;
        this.firstquestion = firstquestion;
        this.instanceId = instanceId;
        this.stoppedloading = false;
        this.isrunning = false;
        this.useQueue = useQueue;
        this.quizstore = quizstore;
        this.resultid = resultid;
        console.debug(`QueueLoader#constructor`);
    }
    get queue() {
        if (this.useQueue) {
            return this._queue;
        }
        else {
            return this._list;
        }
    }
    load_list(startquiz, isaudit = false) {
        const { svc, firstquestion, numberofquestions, resultid, quizstore, _list } = this;
        const queue = _list;
        setLoaded(quizstore)({ loaded: false });
        const totalquestions = numberofquestions + firstquestion;
        this.isrunning = totalquestions > 0;
        if (!this.isrunning) {
            return;
        }
        setStopLoading(quizstore)({
            stopLoading: () => {
                this.stopLoading();
            }
        });
        queue.clear();
        const { cursor: startcursor } = startquiz;
        let loadDelay = 1000;
        const fullsteps = Math.floor(numberofquestions / STEP);
        const countremaining = numberofquestions % STEP;
        const parmstream = new Observable((observer) => {
            let cursor = firstquestion;
            logQuiz.debug(() => `startcursor: ${startcursor}; cursor: ${cursor}; fullsteps: ${fullsteps}; remaining: ${countremaining}`);
            for (let steps = 0; steps < fullsteps; ++steps) {
                const parms = {
                    qrlocator: '',
                    select: {
                        start: cursor,
                        count: STEP,
                        resultid
                    }
                };
                observer.next(parms);
                cursor += STEP;
            }
            if (countremaining > 0) {
                const parms = {
                    qrlocator: '',
                    select: {
                        start: cursor,
                        count: countremaining,
                        resultid
                    }
                };
                observer.next(parms);
                cursor += countremaining;
            }
            observer.complete();
        });
        let firstQuestion = () => {
            if (queue.length > startcursor - firstquestion) {
                const question = queue.elementAtIndex(startcursor - firstquestion);
                if (!question.starttime) {
                    question.starttime = timesync.now().toJSON();
                }
                question.status = isaudit ? 'feedback' : 'question';
                setCursor(quizstore)({ cursor: startcursor - firstquestion });
                setQuestion(quizstore)({ question });
                loadDelay = 2000;
                firstQuestion = () => undefined;
            }
        };
        this.loader = parmstream
            .pipe(concatMap(async (parms) => {
            await waitFor(loadDelay);
            logQuiz.debug(() => `get_questions(${JSON.stringify(parms)})`);
            if (!this.stoppedloading) {
                return svc.get_questions(parms);
            }
            return [];
        }), finalize(() => {
            setLoaded(quizstore)({ loaded: true });
            setStopLoading(quizstore)({ stopLoading: () => undefined });
        }))
            .subscribe((questions) => {
            if (this.stoppedloading) {
                return;
            }
            questions.forEach((question) => {
                const { cursor } = question;
                if (cursor - firstquestion === queue.length) {
                    queue.add(fixup(question, isaudit));
                }
            });
            firstQuestion();
        });
    }
    load_queue(startquiz, isaudit = false) {
        const { svc, numberofquestions, quizstore, _queue } = this;
        const queue = _queue;
        this.isrunning = numberofquestions > 0;
        if (!this.isrunning) {
            return;
        }
        logQuiz.debug(() => `started QueueLoader#load_questions for ${this.instanceId}`);
        this.stoppedloading = false;
        queue.clear();
        setStopLoading(quizstore)({
            stopLoading: () => {
                this.stopLoading();
            }
        });
        let { cursor } = startquiz;
        const { status } = startquiz;
        const startcursor = cursor;
        const parmstream = new Observable((observer) => {
            while (cursor < numberofquestions && !this.stoppedloading) {
                const parms = {
                    qrlocator: '',
                    select: {
                        begin: cursor,
                        end: Math.min(cursor + 6, numberofquestions)
                    }
                };
                observer.next(parms);
                cursor += 7;
            }
            observer.complete();
        });
        this.loader = parmstream
            .pipe(concatMap(async (parms) => {
            await waitFor(1000);
            logQuiz.debug(() => `get_questions(${JSON.stringify(parms)})`);
            if (!this.stoppedloading) {
                const questions = await svc.get_questions(parms);
                return questions;
            }
            return [];
        }), finalize(() => {
            setStopLoading(quizstore)({ stopLoading: undefined });
        }))
            .subscribe((questions) => {
            if (this.stoppedloading) {
                return;
            }
            questions.forEach((question) => {
                if (question.cursor === startcursor) {
                    setCursor(quizstore)({ cursor: 0 });
                    question.starttime = timesync.now().toJSON();
                    question.status = status;
                    const fixedQuestion = question.questiontype === 'MCQuestion' ? fixup(question, isaudit) : question;
                    setQuestion(quizstore)({ question: fixedQuestion });
                }
                else {
                    queue.enqueue(question);
                }
            });
        });
    }
    load_questions(startquiz, isaudit = false) {
        if (this.useQueue) {
            this.load_queue(startquiz, isaudit);
        }
        else {
            this.load_list(startquiz, isaudit);
        }
    }
    stopLoading() {
        logQuiz.debug(() => `QuestionQueue#stopLoading for ${this.instanceId}`);
        const { quizstore } = this;
        if (this.stoppedloading) {
            return;
        }
        this.stoppedloading = true;
        this.loader && this.loader.unsubscribe();
        this.loader = undefined;
        this.isrunning = false;
        setQuestion(quizstore)({ question: undefined });
        this.queue.clear();
        setStopLoading(quizstore)({ stopLoading: undefined });
    }
}
export const allqueues = new Map();
export function clearLoaders() {
    allqueues.forEach((loader) => {
        loader.stopLoading();
    });
    allqueues.clear();
}
export const allmpequeues = new Map();
export function clearMPELoaders() {
    allmpequeues.forEach((loaders) => {
        loaders.forEach((loader) => loader.stopLoading());
    });
    allmpequeues.clear();
}
